<script setup lang="ts">
import type { IProductEntity } from '~/entities';

import { computed, ref } from 'vue';
import { MMText } from '@mm-frontend/mm-web-ui-kit';
import {
  ButtonTertiaryOutlined,
  IconCart,
  IconDot,
  IconRating,
  IconFavorite,
  IconFavoriteFilled,
} from '@/features/ui-kit';
import { DateFormatter, Plural } from '#shared/helpers';
import { useUserStore } from '~/features/store';
import { usePopupAdultConfirm } from '../features';
import { IconEyeCrossed2 } from '~/features/ui-kit';

const props = withDefaults(
  defineProps<{
    product: IProductEntity;
    variant?: 'slim' | 'default';
    loading?: boolean;
  }>(),
  {
    variant: 'default',
  }
);

const emit = defineEmits(['click', 'like', 'add-to-cart']);

// Экспортируем ref для родительского компонента (для аналитики видимости)
const productCardEl = ref<HTMLElement>();

const { isAdult, isAdultChecked } = useUserStore();
defineExpose({
  productCardEl,
});

const isAdultContentBlocked = computed(() => props.product.isAdult && !isAdult.value);

const discount = computed(() => {
  // Используем готовую скидку из API, если она есть
  if (props.product.discountPercent !== undefined) {
    return props.product.discountPercent;
  }

  // Иначе вычисляем скидку как раньше
  if (
    !props.product.sellPrice ||
    !props.product.fullPrice ||
    props.product.sellPrice >= props.product.fullPrice
  )
    return null;
  return -Math.round(
    ((props.product.fullPrice - props.product.sellPrice) / props.product.fullPrice) * 100
  );
});

const isFullPriceShown = computed(() => {
  return (
    props.product.sellPrice &&
    props.product.fullPrice &&
    props.product.sellPrice < props.product.fullPrice
  );
});

const onFavoriteClick = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  emit('like', props.product);
};

const onAddToCartClick = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  if (!props.product.isAdult) {
    emit('add-to-cart', props.product);
    return;
  }

  /** товар для взрослых */
  if (!isAdultChecked.value) {
    showPopupAdultConfirm();
    return;
  }

  if (!isAdult.value) return;

  emit('add-to-cart', props.product);
};

const { showPopupAdultConfirm } = usePopupAdultConfirm();
const onCardClick = (isMetaOrCtrlPressed?: boolean) => {
  if (!props.product.isAdult) {
    emit('click', props.product, isMetaOrCtrlPressed);
    return;
  }

  /** товар для взрослых */
  if (!isAdultChecked.value) {
    showPopupAdultConfirm();
    return;
  }

  if (!isAdult.value) return;

  emit('click', props.product, isMetaOrCtrlPressed);
};

const badges = computed(() => props.product.badges?.slice(0, 2) || []);

const addToCartButtonLabel = computed(() => {
  if (!props.product.inStock) return 'Нет в наличии';
  if (!props.product.nearestDelivery) return 'В корзину';

  return DateFormatter.relativeFromNow(props.product.nearestDelivery).value;
});
</script>

<template>
  <div
    ref="productCardEl"
    class="product-card"
    :class="{ 'product-card_out-of-stock': !product.inStock }"
    data-test-id="item__product-card"
    @click.exact="onCardClick(false)"
    @click.meta="onCardClick(true)"
    @click.ctrl="onCardClick(true)"
  >
    <div class="product-card__image">
      <div class="product-card__badges">
        <div
          v-for="badge in badges"
          :key="badge.id"
          class="product-card__badge"
          :style="{ backgroundColor: badge.backgroundColor, color: badge.textColor }"
        >
          <MMText
            class="product-card__badge-text"
            font="Caption S/Medium"
            font-mobile-or-tablet="Caption S/Regular"
            :style="{ color: badge.textColor }"
            data-test-id="text__product-badge"
          >
            {{ badge.text }}
          </MMText>
        </div>
      </div>

      <img
        :src="product.photo?.link?.high || product.photo?.link?.low || ''"
        :alt="product.title || ''"
        class="product-card__image__img"
        :class="{ 'product-card__image__img_blured': isAdultContentBlocked }"
      />

      <div
        v-if="!isAdultContentBlocked"
        class="product-card__favourite"
        :title="product.isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'"
        data-test-id="button__add-to-favorites"
        @click="onFavoriteClick"
      >
        <IconFavoriteFilled v-if="product.isFavorite" class="favourite-icon" color="accented" />
        <IconFavorite v-else class="favourite-icon" color="primary" />
      </div>

      <div class="product-card__image__adult-banner" v-if="isAdultContentBlocked">
        <IconEyeCrossed2 size="24" />
        <MMText>Товар для взрослых</MMText>
      </div>
    </div>

    <div class="product-card__info">
      <MMText
        class="product-card__sell-price"
        font="Title S/Semibold"
        font-mobile-or-tablet="Body L/Semibold"
        :color="product.inStock ? 'primary' : 'secondary'"
        data-test-id="text__price"
      >
        <CurrencyFormatter :value="product.sellPrice" hide-kopecks />
        <span v-if="discount" class="product-card__discount">
          <MMText font="Caption S/Medium" color="accented">{{ `${discount} %` }}</MMText>
        </span>
      </MMText>

      <MMText
        v-if="isFullPriceShown"
        :color="product.inStock ? 'secondary' : 'disabled'"
        class="product-card__full_price"
        font="Body M/Regular"
        font-mobile-or-tablet="Body S/Regular"
        data-test-id="text__old-price"
      >
        <CurrencyFormatter :value="product.fullPrice ?? 0" hide-kopecks />
      </MMText>

      <MMText
        class="product-card__title"
        font="Body M/Medium"
        font-mobile-or-tablet="Body S/Regular"
        data-test-id="text__product-name"
        :color="product.inStock ? 'primary' : 'secondary'"
      >
        {{ product.title || 'Без названия' }}
      </MMText>

      <MMText
        font="Body S/Regular"
        color="tertiary"
        data-test-id="text__orders"
        class="product-card__rating"
      >
        <template v-if="product.rating || product.feedbackQuantity">
          <IconRating class="rating-icon" color="warning" :size="14" />
          <span v-if="product.rating" data-test-id="text__rating">
            {{ product.rating.toFixed(1) }}
          </span>
          <IconDot
            v-if="product.rating && product.feedbackQuantity"
            class="product-card__rating-dot"
            color="tertiary"
          />
          <span v-if="product.feedbackQuantity" class="product-card__rating-feedback-count">
            {{ Plural.with(product.feedbackQuantity, 'отзыв|отзыва|отзывов') }}
          </span>
        </template>
        <template v-else-if="product.ordersQuantity">
          {{ Plural.with(product.ordersQuantity, 'заказ|заказа|заказов') }}
        </template>
        <span v-else data-test-id="text__status-novelty">Новинка!</span>
      </MMText>
    </div>

    <ButtonTertiaryOutlined
      class="product-card__button"
      w100
      :disabled="!product.inStock"
      :loading="loading"
      data-test-id="button__add-to-cart"
      @click="onAddToCartClick"
    >
      <IconCart v-if="product.inStock" class="cart-icon" :size="20" />
      {{ addToCartButtonLabel }}
    </ButtonTertiaryOutlined>
  </div>
</template>

<style scoped lang="stylus">
.product-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  cursor: pointer;
  box-sizing border-box

  & * {
    box-sizing border-box
  }
}

.product-card__image {
  position: relative;
  aspect-ratio: 3/4;
  background: hsla(220, 4%, 48%, 0.1);
  overflow: hidden;
  border-radius: 8px;

  &__img {
    aspect-ratio: 3/4;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 8px;

    &_blured {
      filter: blur(13px);
    }
  }

  &__adult-banner {
    display flex
    flex-direction column
    justify-content center
    align-items center
    gap 16px
    position absolute
    inset 0
  }
}

.product-card__favourite {
  position: absolute;
  cursor: pointer;
  right: 8px;
  top: 8px;
  z-index: 2;
  font-size 24px
}

.favourite-icon {
  transition: all 0.2s ease;

  &--active {
    transform: scale(1.1);
  }
}

.product-card__badges {
  display: flex;
  flex-wrap: wrap;
  position: absolute;
  bottom: 4px;
  left: 4px;
  gap: 4px;
  z-index: 2;
}

.product-card__badge {
  display: flex;
  height: 16px;
  padding: 4px;
  align-items: center;
  border-radius: 8px;
  max-width: 124px;

  &-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  +Media(Laptop, Desktop, DesktopMax) {
    max-width: 200px;
  }

  > * {
    line-height: unset;
  }
}

.product-card__sell-price {
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-card__discount {
  height: 16px;
  padding: 1px 4px;
  border-radius: 8px;
  background: #FFE8E5;
  color: #FF462D;
}

.product-card__info {
  padding: 4px 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.product-card__full_price {
  text-decoration: line-through;
  text-decoration-color: #FF462D;
}

.product-card__title {
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-card__button {
  margin-top: auto;

  :deep(div::first-letter) {
    text-transform: uppercase;
  }
}

.product-card__rating {
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-card__rating-dot {
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-card__rating-feedback-count {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cart-icon {
  margin-right: 4px;
}

.rating-icon {
  color: #FFB800;
}

.product-card_out-of-stock {
  .product-card__image {
    background: #F7F7F7;
  }

  img {
    opacity: 0.5;
  }
}
</style>
